import { EmailEventType, EmailOffsetUnit, EmailSendRuleKind } from '@/types/touchPoint'
import type { EmailSendRuleDto } from '@/types/touchPoint'
import { formatEventDateTime } from './dateTimeFormatters'

const ANCHOR_LABELS: Record<EmailEventType, string> = {
  [EmailEventType.WebRegistered]: 'registration',
  [EmailEventType.OnlineEventStart]: 'event start',
  [EmailEventType.OnlineEventEnd]: 'event end',
  [EmailEventType.AssetDownloaded]: 'download',
  [EmailEventType.MeetingBooked]: 'meeting',
  [EmailEventType.DemoBooked]: 'demo',
  [EmailEventType.OnlineEventAttended]: 'attendance',
  [EmailEventType.OnlineEventMissed]: 'no-show',
  // ORB-536. Qualified rather than a bare 'event start' — this anchor fires on the same instant as
  // OnlineEventStart, so the two rules are otherwise indistinguishable in a list of activations.
  [EmailEventType.OnlineEventStartRegistrants]: 'event start (registrants)',
  // ORB-508. Reads as "1 day after their email click" — the possessive matters, because this anchor
  // is per-recipient and the surrounding copy is otherwise ambiguous with the blast's own send time.
  [EmailEventType.EmailClicked]: 'their email click',
}

const UNIT_LABELS: Record<EmailOffsetUnit, [string, string]> = {
  [EmailOffsetUnit.Minute]: ['minute', 'minutes'],
  [EmailOffsetUnit.Hour]: ['hour', 'hours'],
  [EmailOffsetUnit.Day]: ['day', 'days'],
}

/**
 * Render an EmailSendRule as a humanized string for list views and editor previews.
 * Examples:
 *   "On registration"
 *   "5 days before event start"
 *   "1 hour after meeting"
 *   "Fires Tue, May 15, 9:00 AM PT"
 */
export function formatEmailRule(rule: EmailSendRuleDto): string {
  if (rule.Kind === EmailSendRuleKind.Scheduled) {
    // Tz-aware rules (ORB-352) carry a local wall-clock + IANA zone; the absolute instant is
    // server-computed, so before save SendAtAbsolute is null. Render from whichever is present.
    if (rule.ScheduledSendLocalTime) {
      const when = formatEventDateTime(rule.ScheduledSendLocalTime)
      return rule.ScheduledSendTimeZone ? `Fires ${when} (${rule.ScheduledSendTimeZone})` : `Fires ${when}`
    }
    if (rule.SendAtAbsolute) return `Fires ${formatEventDateTime(rule.SendAtAbsolute)}`
    return '—'
  }

  if (rule.Kind === EmailSendRuleKind.Event) {
    const { EventType, OffsetValue, OffsetUnit } = rule
    if (!EventType || OffsetValue == null || !OffsetUnit) return '—'

    const anchorLabel = ANCHOR_LABELS[EventType]
    if (OffsetValue === 0) {
      return `On ${anchorLabel}`
    }
    const abs = Math.abs(OffsetValue)
    const [singular, plural] = UNIT_LABELS[OffsetUnit]
    const unitLabel = abs === 1 ? singular : plural
    const direction = OffsetValue < 0 ? 'before' : 'after'
    return `${abs} ${unitLabel} ${direction} ${anchorLabel}`
  }

  return '—'
}
